<template>
  <div>
    <div v-if="showAddTeacherLink" class="text-end">
      <a @click="showTeacherForm = true">Add New Teacher</a>
    </div>

    <v-autocomplete
      v-model="selectedTeacher"
      :items="teachersWithFullName"
      item-text="fullName"
      item-value="id"
      :label="label"
      :no-data-text="searchInput ? 'No data available' : ''"
      placeholder="Enter teacher's name or email"
      clearable
      return-object
      :rules="[requiredRule]"
      @input="updateValue"
      @update:search-input="updateSearchInput"
      @change="$emit('change', selectedTeacher)"
      v-bind="$attrs"
    >
      <template v-slot:item="{ item }">
        <v-list-item-content>
          <v-list-item-title>
            {{ item.fullName }} {{ item.email ? `(${item.email})` : "" }}
            {{
              getCenterName(item.centerId)
                ? `@ ${getCenterName(item.centerId)}`
                : ""
            }}
          </v-list-item-title>
        </v-list-item-content>
      </template>
    </v-autocomplete>
    <v-dialog v-model="showTeacherForm" max-width="600px">
      <teacher-form
        :centers="myCenters"
        @submit="addNewTeacher"
        @close="showTeacherForm = false"
      />
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import _ from "lodash";
import TeacherForm from "@/components/teacher/NewTeacherForm.vue";

export default {
  components: {
    TeacherForm,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    centerId: {
      type: String,
      required: false,
      default: () => null,
    },
    activeOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    classroomIds: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: "Search Teacher",
      required: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    showAddTeacherLink: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapGetters(["globalCenter", "myCenters", "getCenterName"]),
    teachersWithFullName() {
      if (this.teachers) {
        return this.teachers.map((teacher) => ({
          ...teacher,
          fullName: `${teacher.firstName} ${teacher.lastName}`,
        }));
      } else {
        return [];
      }
    },
  },
  data() {
    return {
      selectedTeacher: this.value,
      teachers: this.value && this.value.id ? [this.value] : [],
      requiredRule: (value) => {
        if (this.required) {
          return !!value || "Teacher selection is required.";
        }
        return true;
      },
      showTeacherForm: false,
      searchInput: "",
    };
  },
  mounted() {
    this.debouncedFetchTeachers = _.debounce(this.fetchTeachers, 300);
    if (this.selectedTeacher && this.selectedTeacher.id) {
      this.teachers.push(this.selectedTeacher);
    } else {
      this.teachers = []; // Reset teachers to an empty array if selectedTeacher is null
    }
  },
  watch: {
    value(newValue) {
      this.selectedTeacher = newValue;
      if (this.selectedTeacher) {
        this.teachers = [this.selectedTeacher];
      }
    },
  },
  methods: {
    ...mapActions(["addErrorMessage"]),
    updateSearchInput(input) {
      this.searchInput = input;
      this.debouncedFetchTeachers(input);
    },
    async fetchTeachers(searchInput) {
      if (!searchInput) {
        return;
      }

      const response = await this.$axios.get("/teacher", {
        params: {
          "include-all-centers": true, // We want all teachers the user has access to.
          searchTerms: searchInput,
          size: 50,
        },
      });

      const content = response.data.content;
      this.teachers = [...new Set([...this.teachers, ...content])];
    },
    async addNewTeacher(newTeacher) {
      this.showTeacherForm = false;
      // Assuming "newTeacher" is the object that you get from the form
      // containing all the necessary properties for a teacher
      // Append this new teacher to your "teachers" data property
      const savedTeacher = await this.saveTeacher(newTeacher);
      if (savedTeacher) {
        this.teachers = [...this.teachers, savedTeacher];
      }
    },
    async saveTeacher(teacher) {
      return await this.$axios
        .post("/teacher/update", teacher)
        .then((resp) => {
          const savedTeacher =
            resp.data && resp.data.data ? resp.data.data : null;

          return savedTeacher;
        })
        .catch((error) => {
          this.addErrorMessage(error.message);
        });
    },
    updateValue(value) {
      this.$emit("input", value);
    },
  },
};
</script>
